import { CSSProperties, ReactNode } from 'react';
import {
    Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableHead, TableRow, Chip, Stack,
    Tooltip as MuiTooltip,
} from '@mui/material';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { Check, CheckStatus, FunnelStep, PoolHealth, n0, num, formatMoney } from './format';

/**
 * Presentational panels shared by the Organic and Ads pool analytics tabs. Extracted verbatim from
 * organic/OrganicRecap.tsx (which now imports them and renders exactly as before); everything
 * pool-specific (labels, empty-state text, colours) comes in as props.
 */

export const SectionCard = ({ title, subtitle, tags, children }: {
    title: string; subtitle?: ReactNode; tags?: ReactNode; children: ReactNode;
}) => (
    <Card sx={{ mb: 3 }}>
        <CardContent>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: subtitle ? 0.25 : 1.5 }}>
                <Typography variant="h6" sx={{ mr: 1 }}>{title}</Typography>
                {tags}
            </Stack>
            {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{subtitle}</Typography>
            )}
            {children}
        </CardContent>
    </Card>
);

/** day1 = works on today's data · p1 = needs the pool (organic P1 / paid shadow-canary) · posthog =
 *  PostHog-measured · nightly = needs a Google Ads nightly pull · stage2 = needs the paid canary. */
export type TagKind = 'day1' | 'p1' | 'posthog' | 'nightly' | 'stage2';
const TAG_COLOR: Record<TagKind, 'success' | 'primary' | 'info' | 'warning' | 'secondary'> = {
    day1: 'success', p1: 'primary', posthog: 'info', nightly: 'warning', stage2: 'secondary',
};

export const Tag = ({ label, kind }: { label: string; kind: TagKind }) => (
    <Chip
        size="small"
        variant="outlined"
        label={label}
        color={TAG_COLOR[kind]}
        sx={{ fontSize: 11, height: 20 }}
    />
);

export const EmptyState = ({ title, children }: { title: string; children?: ReactNode }) => (
    <Box sx={{
        border: 1, borderStyle: 'dashed', borderColor: 'divider', borderRadius: 1, p: 2,
        backgroundImage: 'repeating-linear-gradient(135deg, rgba(128,128,128,.06) 0 6px, transparent 6px 12px)',
    }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{title}</Typography>
        {children && <Typography variant="body2" color="text.secondary">{children}</Typography>}
    </Box>
);

export const Tile = ({ label, value, detail, muted }: {
    label: string; value: string; detail?: string; muted?: boolean;
}) => (
    <Card sx={{ flex: '1 1 170px', minWidth: 170 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums', color: muted ? 'text.disabled' : undefined }}>
                {value}
            </Typography>
            {detail && <Typography variant="caption" color="text.secondary">{detail}</Typography>}
        </CardContent>
    </Card>
);

// ── "Is it working?" scorecard ────────────────────────────────────────────────

export interface StatusPalette { good: string; warning: string; critical: string; waiting: string }

export const statusColor = (p: StatusPalette, s: CheckStatus) =>
    s === 'pass' ? p.good : s === 'watch' ? p.warning : s === 'fail' ? p.critical : p.waiting;
export const STATUS_LABEL: Record<CheckStatus, string> = { pass: 'Pass', watch: 'Watch', fail: 'Fail', waiting: 'Not enough data yet' };

export const ChecksTable = ({ checks, p, nowLabel = 'Now', statusLabel = STATUS_LABEL }: {
    checks: Check[]; p: StatusPalette; nowLabel?: string; statusLabel?: Record<CheckStatus, string>;
}) => (
    <Table size="small">
        <TableHead>
            <TableRow>
                <TableCell>Check</TableCell>
                <TableCell>Pass at</TableCell>
                <TableCell align="right">{nowLabel}</TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {checks.map(c => (
                <TableRow key={c.key}>
                    <TableCell>{c.label}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{c.target}</TableCell>
                    <TableCell align="right">
                        <MuiTooltip title={statusLabel[c.status]}>
                            <Chip size="small" label={c.status === 'waiting' ? (c.value === '—' ? statusLabel.waiting : c.value) : `${statusLabel[c.status]} · ${c.value}`}
                                sx={{ bgcolor: statusColor(p, c.status), color: c.status === 'waiting' ? 'text.primary' : '#0b0b0b', fontWeight: 600 }} />
                        </MuiTooltip>
                    </TableCell>
                </TableRow>
            ))}
        </TableBody>
    </Table>
);

// ── Funnel bars (one group, scaled on its own) ────────────────────────────────

export const FunnelGroup = ({ title, list, labels, color, unavailableText }: {
    title: string;
    list: FunnelStep[];
    labels: Record<string, string>;
    color: string;
    unavailableText: (step: string) => string;
}) => {
    const max = Math.max(1, ...list.map(r => n0(r.n)));
    let prev: number | null = null;
    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{title}</Typography>
            {list.map(r => {
                const v = num(r.n);
                const conv = v != null && prev ? ` · ${((v / prev) * 100).toFixed(v / prev < 0.1 ? 1 : 0)} % of previous` : '';
                if (v != null) prev = v;
                const unavailable = !r.available || v == null;
                return (
                    <Stack key={r.step} direction="row" spacing={1} sx={{ alignItems: 'center', py: 0.4 }}>
                        <Typography variant="body2" sx={{ width: 230, flexShrink: 0 }}>{labels[r.step] ?? r.step}</Typography>
                        <Box sx={{ flexGrow: 1, height: 16, position: 'relative' }}>
                            {unavailable ? (
                                <Box sx={{ height: 16, border: 1, borderStyle: 'dashed', borderColor: 'divider', borderRadius: 0.5, px: 1, display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {unavailableText(r.step)}
                                    </Typography>
                                </Box>
                            ) : (
                                <MuiTooltip title={`${v?.toLocaleString('en-CA')}${conv}`}>
                                    <Box sx={{ height: 16, width: `${Math.max(1, (100 * (v ?? 0)) / max)}%`, bgcolor: color, borderRadius: '0 4px 4px 0' }} />
                                </MuiTooltip>
                            )}
                        </Box>
                        <Typography variant="body2" sx={{ width: 90, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {v == null ? '—' : v.toLocaleString('en-CA')}
                            {r.n_attributed != null && n0(r.n_attributed) > 0 ? ` (${n0(r.n_attributed)})` : ''}
                        </Typography>
                    </Stack>
                );
            })}
        </Box>
    );
};

// ── FR / EN × at-need / pre-need ──────────────────────────────────────────────

/** revenueStep: the funnel step whose value_cents is revenue ('revenue' organic, 'won' paid). */
export const SplitsTable = ({ rows, revenueStep = 'revenue' }: {
    rows: [string, FunnelStep[] | undefined][]; revenueStep?: string;
}) => {
    const pick = (steps: FunnelStep[] | undefined, step: string) => steps?.find(r => r.step === step);
    const data = rows.map(([label, steps]) => ({
        label,
        qualified: n0(pick(steps, 'qualified')?.n),
        won: n0(pick(steps, 'won')?.n),
        revenue: n0(pick(steps, revenueStep)?.value_cents),
        preneed: label.includes('pre-need'),
    }));
    const tot = data.reduce((a, r) => ({ q: a.q + r.qualified, w: a.w + r.won, rev: a.rev + r.revenue }), { q: 0, w: 0, rev: 0 });
    const wonPct = (w: number, q: number, pre: boolean) => (pre || q < 10 ? '—' : `${Math.round((100 * w) / q)} %`);
    return (
        <Table size="small">
            <TableHead><TableRow>
                <TableCell>Segment</TableCell><TableCell align="right">Qualified</TableCell><TableCell align="right">Won</TableCell>
                <TableCell align="right">Won ÷ qualified</TableCell><TableCell align="right">Revenue</TableCell>
            </TableRow></TableHead>
            <TableBody>
                {data.map(r => (
                    <TableRow key={r.label}>
                        <TableCell>{r.label}</TableCell>
                        <TableCell align="right">{r.qualified}</TableCell>
                        <TableCell align="right">{r.won}</TableCell>
                        <TableCell align="right">{wonPct(r.won, r.qualified, r.preneed)}</TableCell>
                        <TableCell align="right">{formatMoney(r.revenue)}</TableCell>
                    </TableRow>
                ))}
                <TableRow sx={{ '& td': { fontWeight: 600 } }}>
                    <TableCell>Sum of the four</TableCell>
                    <TableCell align="right">{tot.q}</TableCell>
                    <TableCell align="right">{tot.w}</TableCell>
                    <TableCell align="right">{wonPct(tot.w, tot.q, false)}</TableCell>
                    <TableCell align="right">{formatMoney(tot.rev)}</TableCell>
                </TableRow>
            </TableBody>
        </Table>
    );
};

// ── Pool health (get_tracking_pool_health, any pool) ──────────────────────────

export interface PoolChartPalette { grid: string; axis: string; muted: string; critical: string; matched: string; unmatched: string }

export const PoolHealthBody = ({ pool, p, tooltipStyle, holdMinutes = 30 }: {
    pool: PoolHealth; p: PoolChartPalette; tooltipStyle: CSSProperties; holdMinutes?: number;
}) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
        <ResponsiveContainer width="100%" height={240}>
            <BarChart data={pool.hourly.map(h => ({ ...h, hour: `${h.hour_et}h`, mean: n0(h.mean_in_use), max: n0(h.max_in_use) }))}>
                <CartesianGrid vertical={false} stroke={p.grid} />
                <XAxis dataKey="hour" tick={{ fill: p.muted, fontSize: 11 }} interval={2} axisLine={{ stroke: p.axis }} tickLine={false} />
                <YAxis tick={{ fill: p.muted, fontSize: 11 }} axisLine={false} tickLine={false} width={28} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <ReferenceLine y={pool.pool_size_active} stroke={p.critical} strokeDasharray="5 4"
                    label={{ value: `pool ${pool.pool_size_active}`, fill: p.critical, fontSize: 11, position: 'insideTopRight' }} />
                <Bar dataKey="mean" name="Mean in use" fill={p.matched} radius={[4, 4, 0, 0]} />
                <Bar dataKey="max" name="Max in use" fill={p.unmatched} radius={[4, 4, 0, 0]} />
            </BarChart>
        </ResponsiveContainer>
        <Box>
            <Table size="small">
                <TableHead><TableRow><TableCell>Numbers</TableCell><TableCell>Status</TableCell><TableCell align="right">Count</TableCell><TableCell align="right">Junk calls</TableCell></TableRow></TableHead>
                <TableBody>
                    {pool.inventory.map(i => (
                        <TableRow key={`${i.number_type}-${i.status}`}>
                            <TableCell>{i.number_type === 'toll_free' ? 'Toll-free' : 'Local'}</TableCell>
                            <TableCell>{i.status}</TableCell>
                            <TableCell align="right">{n0(i.numbers)}</TableCell>
                            <TableCell align="right">{n0(i.junk_calls)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Table size="small" sx={{ mt: 1 }}>
                <TableBody>
                    <TableRow><TableCell>Blocked claims (exhaustion)</TableCell><TableCell align="right">{n0(pool.exhaustion_events)}</TableCell></TableRow>
                    <TableRow><TableCell>Calls to the pool-full number</TableCell><TableCell align="right">{n0(pool.pool_full_calls)}</TableCell></TableRow>
                    <TableRow><TableCell>Matched / unmatched / junk calls</TableCell><TableCell align="right">{n0(pool.timing.matched_calls)} / {n0(pool.timing.unmatched_calls)} / {n0(pool.timing.junk_calls)}</TableCell></TableRow>
                    <TableRow><TableCell>Assign → call p50 / p90</TableCell><TableCell align="right">{num(pool.timing.delay_p50_min) ?? '—'} / {num(pool.timing.delay_p90_min) ?? '—'} min</TableCell></TableRow>
                    <TableRow><TableCell>{`Matched after the ${holdMinutes} min hold`}</TableCell><TableCell align="right">{n0(pool.timing.calls_after_hold)}</TableCell></TableRow>
                </TableBody>
            </Table>
        </Box>
    </Box>
);
